import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    List,
    ListItem,
    ListItemText,
    Divider,
    Stack,
    Paper,
    Chip,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { useSelector } from 'react-redux';
import { selectCurrentAccessToken } from '../../features/authentication/authSlice';

const DeleteUserDialog = ({ open, onClose, user, allUsers, onDeleteConfirm }) => {
    const [userEvents, setUserEvents] = useState([]);
    const [eventCount, setEventCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [selectedReassignUser, setSelectedReassignUser] = useState('');
    const [error, setError] = useState('');
    const accessToken = useSelector(selectCurrentAccessToken);

    useEffect(() => {
        if (open && user) {
            fetchUserEvents();
        }
    }, [open, user]);

    const fetchUserEvents = async () => {
        setLoading(true);
        setError('');
        try {
            const url = `${process.env.REACT_APP_API_URL}/users/${user.id}/events`;

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });


            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('Failed to fetch user events:');
                console.error('Status:', response.status);
                console.error('Status Text:', response.statusText);
                console.error('Error Data:', errorData);
                console.error('URL:', url);
                throw new Error(`Failed to fetch user events: ${response.status}`);
            }

            const data = await response.json();
            setUserEvents(data.events);
            setEventCount(data.count);
        } catch (err) {
            console.error('Error fetching user events:', err);
            setError(`Tapahtumien hakeminen epäonnistui: ${err.message || 'Tuntematon virhe'}`);
        } finally {
            setLoading(false);
        }
    };

    const handleReassignAndDelete = async () => {
        if (eventCount > 0 && !selectedReassignUser) {
            setError('Valitse käyttäjä, jolle tapahtumat siirretään');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // First, reassign events if there are any
            if (eventCount > 0 && selectedReassignUser) {
                const reassignResponse = await fetch(`${process.env.REACT_APP_API_URL}/users/reassign-events`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({
                        fromUserId: user.id,
                        toUserId: selectedReassignUser,
                    }),
                });

                if (!reassignResponse.ok) {
                    throw new Error('Failed to reassign events');
                }
            }

            // Then delete the user
            await onDeleteConfirm();
            handleClose();
        } catch (err) {
            console.error(err);
            setError('Käyttäjän poistaminen epäonnistui');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedReassignUser('');
        setError('');
        onClose();
    };

    // Filter out the user being deleted from the reassignment options
    const availableUsers = allUsers.filter(u => u.id !== user?.id);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    overflow: 'hidden',
                }
            }}
        >
            <DialogTitle
                sx={{
                    px: { xs: 3, sm: 4 },
                    py: 3,
                    background: 'linear-gradient(135deg, #FFF4E5 0%, #FFE2B5 100%)',
                    color: 'warning.dark',
                }}
            >
                <Stack spacing={1.25}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                display: 'grid',
                                placeItems: 'center',
                                bgcolor: 'rgba(237, 108, 2, 0.12)',
                            }}
                        >
                            <WarningIcon />
                        </Box>
                        <Typography variant="h5" fontWeight={700}>
                            Poista käyttäjä
                        </Typography>
                    </Stack>
                    <Typography variant="body1" sx={{ maxWidth: 760 }}>
                        Tarkista ennen poistamista, täytyykö käyttäjän tapahtumat siirtää toiselle henkilölle.
                        Näet alla poistettavan käyttäjän tapahtumamäärän ja tarvittaessa siirtolistauksen.
                    </Typography>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ px: { xs: 2, sm: 4 }, py: 3, bgcolor: '#FBFCFE' }}>
                {loading && !userEvents.length ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {error && (
                            <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
                                {error}
                            </Alert>
                        )}

                        <Stack spacing={3}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'white',
                                }}
                            >
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={2}
                                    justifyContent="space-between"
                                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                                >
                                    <Box>
                                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                                            Poistettava käyttäjä
                                        </Typography>
                                        <Typography variant="h6" fontWeight={700}>
                                            {user?.name || 'Tuntematon käyttäjä'}
                                        </Typography>
                                        {user?.username && (
                                            <Typography color="text.secondary">
                                                {user.username}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Chip
                                        label={eventCount === 1 ? '1 tapahtuma' : `${eventCount} tapahtumaa`}
                                        color={eventCount > 0 ? 'warning' : 'success'}
                                        variant={eventCount > 0 ? 'filled' : 'outlined'}
                                        sx={{ fontWeight: 700, borderRadius: 2 }}
                                    />
                                </Stack>
                            </Paper>

                            {eventCount > 0 ? (
                                <>
                                    <Alert severity="warning" sx={{ borderRadius: 3 }}>
                                        Tällä käyttäjällä on <strong>{eventCount}</strong> tapahtumaa. Valitse käyttäjä,
                                        jolle omistajuus siirretään ennen poistamista.
                                    </Alert>

                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2.5,
                                            borderRadius: 3,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            bgcolor: 'white',
                                        }}
                                    >
                                        <Stack spacing={2}>
                                            <Box>
                                                <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                                                    Siirrä tapahtumat toiselle käyttäjälle
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Valittu käyttäjä saa kaikki alla näkyvät tapahtumat omiin tapahtumalistoihinsa.
                                                </Typography>
                                            </Box>

                                            <FormControl fullWidth>
                                                <InputLabel id="reassign-user-label">Vastaanottava käyttäjä</InputLabel>
                                                <Select
                                                    labelId="reassign-user-label"
                                                    value={selectedReassignUser}
                                                    onChange={(e) => setSelectedReassignUser(e.target.value)}
                                                    label="Vastaanottava käyttäjä"
                                                    required
                                                >
                                                    {availableUsers.map((u) => (
                                                        <MenuItem key={u.id} value={u.id}>
                                                            {u.name} ({u.username})
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Stack>
                                    </Paper>

                                    <Paper
                                        elevation={0}
                                        sx={{
                                            borderRadius: 3,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            bgcolor: 'white',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                                            <Typography variant="subtitle1" fontWeight={700}>
                                                Siirrettävät tapahtumat
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Tarkista lista ennen käyttäjän poistamista.
                                            </Typography>
                                        </Box>

                                        <Box sx={{ maxHeight: 300, overflowY: 'auto', bgcolor: '#FCFCFD' }}>
                                            <List dense disablePadding>
                                                {userEvents.map((event, index) => (
                                                    <Box key={event.id}>
                                                        <ListItem sx={{ px: 2.5, py: 1.5, alignItems: 'flex-start' }}>
                                                            <ListItemText
                                                                primary={
                                                                    <Typography fontWeight={600}>
                                                                        {event.nimi}
                                                                    </Typography>
                                                                }
                                                                secondary={
                                                                    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            Aloitus: {new Date(event.aloitus).toLocaleDateString('fi-FI')}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            ID: {event.id}
                                                                        </Typography>
                                                                    </Stack>
                                                                }
                                                            />
                                                        </ListItem>
                                                        {index < userEvents.length - 1 && <Divider />}
                                                    </Box>
                                                ))}
                                            </List>
                                        </Box>
                                    </Paper>
                                </>
                            ) : (
                                <Alert severity="info" sx={{ borderRadius: 3 }}>
                                    Käyttäjällä ei ole tapahtumia. Käyttäjä voidaan poistaa ilman siirtoa.
                                </Alert>
                            )}
                        </Stack>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 4 }, py: 2.5, bgcolor: 'white' }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    variant="outlined"
                >
                    Peruuta
                </Button>
                <Button
                    onClick={handleReassignAndDelete}
                    disabled={loading || (eventCount > 0 && !selectedReassignUser)}
                    variant="contained"
                    color="error"
                    sx={{ minWidth: 160, fontWeight: 700 }}
                >
                    {loading ? <CircularProgress size={24} /> : 'Poista käyttäjä'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteUserDialog;
